import {FC, useEffect, useMemo, useRef, useState} from 'react';
import {IRaffleTicketsData, PaymentResult} from '@/utils/types';
import {Box, Typography, useMediaQuery} from '@mui/material';
import Button from '@components/Button';
import Modal from '@/shared/components/Modal';
import {ReactComponent as TicketIcon} from '@icons/sidebar/ticket.svg';
import {ReactComponent as ClockIcon} from '@icons/clock.svg';
import {ReactComponent as CalendarIcon} from '@icons/calendar.svg';
import {ReactComponent as DangerIcon} from '@icons/danger.svg';
import Placeholder from '@/assets/images/plh.png';
import {raffle} from './raffle.styles';
import RaffleBuyTicketModal from './RaffleBuyTicketModal';
import RaffleTicketItem from './RaffleTicketItem';
import {useAppSelector} from '@/store/hooks';
import {getCurrentEventSelector} from '@/store/organization-event';
import dayjs from 'dayjs';
import {MERIDIEM_HOUR_FORMAT, MM_DD_YYYY_SLASH} from '@/utils/constants';
import {useNavigate, useParams} from 'react-router-dom';
import {ROUTER_URL} from '@/utils/enums';
import {useDispatch, useSelector} from 'react-redux';
import {setInitialRaffleTickets, setLoading} from '@/store/raffle';
import {getRaffleListSelector, getRaffleLoadingSelector} from '@/store/raffle/selectors';
import Loader from '@/shared/components/Loader';
import {useAuctionTimer} from '@/utils/hooks';
import {currencyFormatter} from '@/utils/helpers/currency';
import {OrganizationStripeService} from '@/utils/services/organization-stripe.services';
import {toast} from 'react-toastify';
import {getErrorMessage} from '@/utils/helpers';
import {userAPI} from '@/core/api';
import {getUserSelector} from '@/store/user';
import NeedBeLoggedModal from '@/components/User/NeedBeLogged';

const Raffle: FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {slug} = useParams();

  const user = useSelector(getUserSelector);
  const event = useAppSelector(getCurrentEventSelector);
  const raffleTicketsData = useSelector(getRaffleListSelector);

  const remainingQty = event?.raffleInfo?.remainingQty;
  const isSoldOut = remainingQty !== null && remainingQty !== undefined && remainingQty <= 0;

  const isLoading = useSelector(getRaffleLoadingSelector);

  const isLaptop = useMediaQuery('(min-width: 1200px)');
  const {hasEnded} = useAuctionTimer(event?.raffleInfo?.endRaffleDate);

  const [isShowChangePhotoModal, setIsShowChangePhotoModal] = useState(false);
  const [isNeedBeLoggedModalOpen, setIsNeedBeLoggedModalOpen] = useState(false);

  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const totalPrice = useMemo(() => {
    return raffleTicketsData.reduce((sum: number, tier: IRaffleTicketsData) => {
      return sum + tier.amount * tier.price;
    }, 0);
  }, [raffleTicketsData]);

  const handleBuyTicketModalShow = () => {
    if (!user) {
      setIsNeedBeLoggedModalOpen(true);
      return;
    }
    // eslint-disable-next-line no-unused-expressions
    isLaptop
      ? setIsShowChangePhotoModal(!isShowChangePhotoModal)
      : navigate(`/${slug}/${ROUTER_URL.USER_RAFFLE_BUY_TICKET}`);
  };

  useEffect(() => {
    dispatch(setLoading(true));

    if (event?.raffleInfo?.tickets && !raffleTicketsData.length) {
      const initialRaffleTickets = event.raffleInfo.tickets.map((item) => ({
        ...item,
        amount: 0,
      }));

      dispatch(setInitialRaffleTickets(initialRaffleTickets));
    } else if (event) {
      dispatch(setLoading(false));
    }
  }, [event?.id, dispatch]);

  const MAX_RETRIES = 5;

  const checkPaymentStatus = async (paymentId: string, retryCount = 0, toastId?: string | number) => {
    if (retryCount === 0) {
      toastId = toast.loading('Checking payment status...');
    }

    try {
      const {data} = await OrganizationStripeService.checkPaymentStatus(+paymentId, userAPI);

      if (data.status === PaymentResult.COMPLETED) {
        if (toastId) toast.dismiss(toastId);
        toast.success('Payment completed successfully!');
      } else if (data.status === PaymentResult.PENDING) {
        if (retryCount < MAX_RETRIES) {
          timeoutRef.current = setTimeout(() => {
            checkPaymentStatus(paymentId, retryCount + 1, toastId);
          }, 3000);
        } else {
          if (toastId) toast.dismiss(toastId);
          toast.info('Payment is still being processed. Please check the status in a few minutes.');
        }
      } else if (data.status === PaymentResult.FAILED) {
        if (toastId) toast.dismiss(toastId);
        toast.error('Payment failed!');
      } else if (data.status === PaymentResult.REFUNDED) {
        if (toastId) toast.dismiss(toastId);
        toast.error('Payment refunded!');
      }
    } catch (error) {
      if (toastId) toast.dismiss(toastId);
      toast.error(getErrorMessage(error));
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentId = params.get('paymentId');

    if (paymentId) {
      checkPaymentStatus(paymentId);

      params.delete('paymentId');
      params.delete('session_id');
      params.delete('organizationId');

      const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
      window.history.replaceState({}, '', newUrl);
    }
    return () => clearTimeout(timeoutRef.current);
  }, []);

  return (
    <Box sx={raffle.wrapper}>
      {isLoading ? (
        <Loader sx={raffle.loader} size={50} />
      ) : (
        <>
          <Box sx={raffle.imageWrapper}>
            {event?.raffleInfo.raffleImage ? (
              <Box sx={raffle.image}>
                <img src={event?.raffleInfo.raffleImage} className="raffle-banner" alt="eventImg" />
              </Box>
            ) : (
              <Box sx={raffle.imageAlt}>
                <img src={Placeholder} className="raffle-banner" alt="eventImg" />
              </Box>
            )}
            <Box sx={raffle.dateWrap}>
              <Typography sx={raffle.dateLabel}>Sales End:</Typography>
              <Typography sx={raffle.dateItem}>
                <CalendarIcon />
                {dayjs(event?.raffleInfo.endRaffleDate).format(MM_DD_YYYY_SLASH)}
              </Typography>
              <Typography sx={raffle.dateItem}>
                <ClockIcon />
                {dayjs(event?.raffleInfo.endRaffleDate).format(MERIDIEM_HOUR_FORMAT)}
              </Typography>
            </Box>
          </Box>
          <Box sx={raffle.columnWrap}>
            <Box sx={raffle.column}>
              <Typography sx={raffle.columnCaption}>Raffle tickets:</Typography>
              <Box sx={raffle.tableHead}>
                <Typography sx={raffle.columnLabel}>Price</Typography>
                <Typography sx={raffle.columnLabel}>Quantity</Typography>
                <Typography sx={raffle.columnLabel}>Subtotal</Typography>
              </Box>
              {raffleTicketsData &&
                raffleTicketsData.map((tier: IRaffleTicketsData) => (
                  <Box key={tier.id} sx={raffle.ticketRow}>
                    <RaffleTicketItem tier={tier} raffleData={raffleTicketsData} />
                  </Box>
                ))}
              <Box sx={raffle.totalWrap}>
                <Typography sx={raffle.totalText}>Total</Typography>
                <Typography sx={raffle.totalText}>{currencyFormatter.format(totalPrice)}</Typography>
              </Box>
              <Button
                startIcon={
                  <Box sx={raffle.iconWrapper}>
                    <TicketIcon />
                  </Box>
                }
                title={
                  hasEnded || event?.hasActiveRaffleWinner
                    ? 'Raffle has ended'
                    : isSoldOut
                    ? 'Sold out'
                    : 'Buy Raffle tickets'
                }
                sx={raffle.buttonCreate}
                onClick={handleBuyTicketModalShow}
                disableRipple
                disabled={event?.hasActiveRaffleWinner || hasEnded || isSoldOut}
              />
            </Box>
            <Box sx={raffle.column}>
              <Typography sx={raffle.columnCaption}>Description:</Typography>
              <Typography sx={raffle.columnText}>
                {event?.raffleInfo.raffleDescription || 'No description available for this raffle.'}
              </Typography>
            </Box>
          </Box>
        </>
      )}
      <Modal onClose={handleBuyTicketModalShow} open={isShowChangePhotoModal} sx={raffle.customeModal}>
        <RaffleBuyTicketModal
          onCloseModal={handleBuyTicketModalShow}
          totalPrice={totalPrice}
          raffleData={raffleTicketsData}
        />
      </Modal>
	  <Modal
        onClose={() => setIsNeedBeLoggedModalOpen(false)}
        open={isNeedBeLoggedModalOpen}
        sx={raffle.modalLogin}
        mainIcon={<DangerIcon />}
      >
        <NeedBeLoggedModal handleClose={() => setIsNeedBeLoggedModalOpen(false)} />
      </Modal>
    </Box>
  );
};

export default Raffle;
